import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { ColorData, ClusterData } from '../../../types';
import {
  forceCenter,
  forceCollide,
  forceRadial,
  forceSimulation,
} from 'd3-force-3d';
import { Instance, Instances } from '@react-three/drei';
import { InstancedMesh, Mesh, Group } from 'three';
import { useExperienceStore } from '../../../store';
import d3ForceCluster from 'd3-force-cluster-3d';
import { CONFIG } from '../../../config';
import { useFrame } from '@react-three/fiber';
import { lerp } from '@/utils/lerp';
import { useSpring, easings } from '@react-spring/three';
import { useMousePosition } from '@/hooks/use-mouse-position';
import { Lights } from './lights';
import { useIsTouch } from '@/hooks/use-is-touch';

const forceSimulationInit = forceSimulation();

interface ColorGroupComponentProps {
  colorsData: Array<ColorData>;
  clusterData: Array<ClusterData>;
}

function setFrames(amount: number) {
  return 1 - Math.pow(0.001, 1 / amount);
}

// Any because
// @TODO fix any CFW-697
function setOpacity(obj: any, opacity: number) {
  // @TODO fix any CFW-697
  obj.children.forEach((child: any) => {
    setOpacity(child, opacity);
  });

  if (obj.material) {
    obj.material.opacity = opacity;
  }
}

function ColorGroupComponent({
  colorsData,
  clusterData,
}: ColorGroupComponentProps) {
  const instanceRef = useRef<InstancedMesh>(null);
  const cubeGroup = useRef<Group>(null);
  const mousePosition = useMousePosition({ normalize: true });
  const isTouch = useIsTouch();
  const saveMousePosition = useRef({
    x: 0,
    y: 0,
  });

  const simulation = useMemo(() => {
    return forceSimulationInit.nodes(colorsData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    activeIndexes,
    updateHoverIndex,
    currentState,
    updateState,
    exploreState,
    updateExploreState,
    activePalette,
    hoverIndex,
    updateActiveIndex,
    showContent,
  } = useExperienceStore((state) => ({
    activeIndexes: state.activeIndexes,
    currentState: state.currentState,
    updateState: state.updateState,
    exploreState: state.exploreState,
    updateExploreState: state.updateExploreState,
    activePalette: state.activePalette,
    updateHoverIndex: state.updateHoverIndex,
    hoverIndex: state.hoverIndex,
    updateActiveIndex: state.updateActiveIndex,
    showContent: state.showContent,
  }));

  const [properties, setProperties] = useState({
    radial: 100,
    spacing: 100,
    alphaDecay: 0.0228,
    clusterStrength: 0.1,
    clusterInertia: 0.1,
    position: { y: 0, z: 0 },
    spacingX: 25,
    segments: 32,
  });

  useEffect(() => {
    simulation.stop();
    simulation.alpha(1);
    simulation.alphaDecay(properties.alphaDecay);
    simulation.numDimensions(3);
    simulation
      .force(
        'cluster',
        d3ForceCluster()
          .centers((node: ColorData) => {
            return clusterData[node.cluster];
          })
          .strength(properties.clusterStrength)
          .centerInertia(properties.clusterInertia)
      )
      .force(
        'r',
        forceRadial(() => properties.radial)
      )
      .force(
        'collide',
        forceCollide()
          .radius((d: ColorData) => d.radius + properties.spacing)
          .iterations(2)
      );

    simulation.restart();
  }, [
    properties.radial,
    properties.spacing,
    properties.alphaDecay,
    simulation,
    clusterData,
    properties.clusterInertia,
    properties.clusterStrength,
  ]);

  const matchingPositions = useMemo(
    () =>
      [...Array(CONFIG.AMOUNT_OF_ITEMS_BEFORE_MATCHING).keys()]
        .map((item) => [
          Math.sin(item / (CONFIG.AMOUNT_OF_ITEMS_BEFORE_MATCHING - 1) - 0.5) *
            properties.spacingX,
          properties.position.y,
          properties.position.z,
        ])
        .reverse(),
    [properties.spacingX, properties.position]
  );

  useEffect(() => {
    simulation.stop();
    simulation.alpha(1);

    simulation
      .nodes(colorsData)
      .numDimensions(3)
      .force('center', forceCenter(0, 0));

    if (currentState === 'start') {
      setProperties((properties) => ({
        ...properties,
        radial: 31,
        spacing: 1,
        alphaDecay: setFrames(175),
      }));
    }

    if (currentState === 'intro') {
      setProperties((properties) => ({
        ...properties,
        radial: 10,
        spacing: 0.1,
      }));
    }

    if (currentState === 'explore') {
      setProperties((properties) => ({
        ...properties,
        alphaDecay: setFrames(350),
      }));
      updateExploreState('orb');
    }

    if (currentState === 'loading') {
      setProperties((properties) => ({
        ...properties,
        radial: 20,
        spacing: 5,
      }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentState]);

  useEffect(() => {
    if (currentState === 'explore') {
      switch (exploreState) {
        case 'orb':
          // Start view orb
          setProperties((properties) => ({
            ...properties,
            alphaDecay: setFrames(100),
            radial: 10,
            spacing: 1,
          }));

          break;
        case 'cluster':
          switch (activeIndexes.length) {
            case 0:
              setProperties((properties) => ({
                ...properties,
                alphaDecay: setFrames(75),
                radial: 20,
                spacing: 1.5,
              }));
              break;
            case 1:
              setProperties((properties) => ({
                ...properties,
                alphaDecay: setFrames(100),
                radial: 25,
                spacing: 1,
              }));
              break;
            case 2:
              setProperties((properties) => ({
                ...properties,
                alphaDecay: setFrames(120),
                radial: 30,
                spacing: 0.5,
              }));
              break;
            case 3:
              setProperties((properties) => ({
                ...properties,
                alphaDecay: setFrames(140),
                radial: 35,
                spacing: 0.25,
              }));
              break;
            default:
              break;
          }
          break;
        default:
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exploreState, activeIndexes]);

  useEffect(() => {
    simulation.nodes(colorsData).on('end', () => {
      if (currentState === 'start') {
        updateState('explore');
        updateExploreState('orb');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentState]);

  function onPointerOverHandler(item: ColorData) {
    if (showContent && isTouch) return;

    if (currentState === 'explore' && exploreState === 'orb') {
      updateExploreState('cluster');

      return;
    }

    if (currentState !== 'explore') return;
    if (activeIndexes.includes(item.index)) return;

    isTouch ? updateActiveIndex(item.index) : updateHoverIndex(item.index);
    updateHoverIndex(item.index);
  }

  useFrame((state) => {
    if (!instanceRef.current) return;

    const time = state.clock.getElapsedTime();

    instanceRef.current.children.forEach((mesh, index) => {
      const currentMesh = mesh.children[0];

      const meshPos = currentMesh.position;

      // Handle position from forces
      if (activeIndexes.includes(index)) {
        // Get the positions of the predefined positions in front of the screen
        if (currentState === 'explore') {
          const pos = matchingPositions[activeIndexes.indexOf(index)];

          meshPos.x = lerp(meshPos.x, pos[0], 0.035);
          meshPos.y = lerp(meshPos.y, pos[1], 0.035);
          meshPos.z = lerp(meshPos.z, pos[2], 0.035);

          const scaleLerp = lerp(
            currentMesh.scale.x,
            isTouch
              ? currentMesh.userData.scale * 4
              : currentMesh.userData.scale * 3.2,
            0.1
          );
          currentMesh.scale.set(scaleLerp, scaleLerp, scaleLerp);
        } else {
        }
      } else {
        meshPos.x = lerp(meshPos.x, colorsData[index].x, 0.07);
        meshPos.y = lerp(meshPos.y, colorsData[index].y, 0.06);
        meshPos.z = lerp(meshPos.z, colorsData[index].z, 0.05);

        if (currentState === 'explore') {
          if (hoverIndex !== index) {
            const scaleLerp = lerp(
              currentMesh.scale.x,
              currentMesh.userData.scale,
              0.03
            );

            currentMesh.scale.set(scaleLerp, scaleLerp, scaleLerp);
          }
        }
      }

      // Handle scaling
      if (currentState === 'start') {
        const scaleLerp = lerp(
          currentMesh.scale.x,
          currentMesh.userData.scale,
          0.05
        );
        currentMesh.scale.set(scaleLerp, scaleLerp, scaleLerp);
      }

      if (currentState === 'intro') {
        const scaleLerp = lerp(
          currentMesh.scale.x,
          currentMesh.userData.scale,
          0.03
        );
        currentMesh.scale.set(scaleLerp, scaleLerp, scaleLerp);
      }

      // Fade away the spheres
      if (currentState === 'loading') {
        if (!activeIndexes.includes(index)) {
          const scaleLerp = lerp(currentMesh.scale.x, 0, 0.05);
          currentMesh.scale.set(scaleLerp, scaleLerp, scaleLerp);
        }
      }

      // Handle floating geometries
      if (currentState === 'explore') {
        meshPos.set(
          meshPos.x + Math.sin(index + time) * 0.01,
          meshPos.y + Math.sin(index + time) * 0.02,
          meshPos.z
        );
      }

      // Handle slow rotating
      currentMesh.rotation.x += 0.002 * Math.sin(index);
      currentMesh.rotation.y += 0.004 * Math.sin(index);
    });

    // Mouse movement
    if (cubeGroup.current) {
      const lerpX = lerp(saveMousePosition.current.x, mousePosition.x, 0.02);
      const lerpY = lerp(saveMousePosition.current.y, mousePosition.y, 0.025);

      saveMousePosition.current.x = lerpX + lerpY;
      saveMousePosition.current.y = lerpY;

      cubeGroup.current.rotation.x = saveMousePosition.current.x * 0.008;

      cubeGroup.current.rotation.y +=
        typeof hoverIndex === 'number' ? 0.0005 : 0.0015;
    }
  });

  useSpring({
    from: { opacity: 0 },
    to: { opacity: 0.87 },
    config: {
      delay: 300,
      duration: 550,
      easing: easings.easeInQuart,
    },
    onChange: ({ value }) => {
      if (!cubeGroup.current) return;

      setOpacity(cubeGroup.current, value.opacity);
    },
  });

  // This is the animation when the balls go away after the selection has been confirmed
  useSpring({
    progress: currentState === 'loading' && activePalette ? 0 : 1,
    config: {
      duration: 2000,
      delay: 1000,
      easing: easings.easeInExpo,
    },
    onChange: ({ value }) => {
      if (!instanceRef.current) return;

      instanceRef.current.children.forEach((mesh, index) => {
        if (activeIndexes.includes(index)) {
          const currentMesh = mesh.children[0];

          currentMesh.scale.set(
            currentMesh.scale.x * value.progress,
            currentMesh.scale.y * value.progress,
            currentMesh.scale.z * value.progress
          );

          currentMesh.position.x = currentMesh.position.x * value.progress;
          currentMesh.position.y = currentMesh.position.y * value.progress;
        }
      });
    },
    onResolve() {
      if (currentState === 'loading') {
        updateState('matched');
      }
    },
  });

  // Safeguard for incase something goes wrong?
  if (currentState === 'matched') return null;

  return (
    <group ref={cubeGroup}>
      {/* <group ref={cubeGroup} position={[-6, 0, 0]}> */}
      {false && <Lights activeIndexes={activeIndexes} />}

      {/* @ts-expect-error typing seems okay but still gives an error :() */}
      <Instances limit={colorsData.length} ref={instanceRef}>
        <sphereGeometry
          args={[1, properties.segments * 2, properties.segments]}
        />

        <meshBasicMaterial transparent />

        {colorsData.map((item, index) => (
          <group
            key={index}
            onPointerOver={() => onPointerOverHandler(item)}
            onPointerDown={() => onPointerOverHandler(item)}
            onPointerOut={() => updateHoverIndex(null)}
          >
            <ColorMesh item={item} />
          </group>
        ))}
      </Instances>
    </group>
  );
}

export const ColorGroup = memo(ColorGroupComponent, () => true);

interface ColorMeshProps {
  item: ColorData;
}

function ColorMesh({ item }: ColorMeshProps) {
  const refMesh = useRef<Mesh>(null);

  const { hoverIndex } = useExperienceStore((state) => ({
    hoverIndex: state.hoverIndex,
  }));

  useSpring({
    scale: hoverIndex === item.index ? item.radius * 1.5 : item.radius,
    config: {
      duration: hoverIndex === item.index ? 500 : 150,
      easing: easings.linear,
    },
    onChange: ({ value }) => {
      const animatedScale = value.scale;

      refMesh.current?.scale.set(animatedScale, animatedScale, animatedScale);
    },
  });

  return (
    <Instance
      scale={item.radius}
      ref={refMesh}
      color={item.color}
      userData={{ scale: item.radius }}
      // attachArray={undefined}
      // attachObject={undefined}
    />
  );
}
